/**
 * Composes redesigned RMA list columns into a TableRow with shared navigation and analytics.
 * When bulk Mark as Done is enabled, prepends a row checkbox derived from the selection store.
 */
import type { MouseEvent } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import TableRow from "../../../../../../../components/table/components/table-row/table-row";
import { Checkbox, CHECKBOX_SIZES } from "../../../../../../../components/inputs/checkbox/checkbox";
import { APP_PATHS } from "../../../../../../../constants/app-paths";
import {
    MANAGE_RETURNS_DESIGN_VERSION,
    trackManageReturnsListEvent,
} from "../../../../../utils/manage-returns-list-mixpanel";
import RmaListItemStore from "../../../../../rma-list-page/stores/rma-item-store";
import TableColumnCustomer from "./components/table-column-customer/table-column-customer";
import TableColumnItems from "./components/table-column-items/table-column-items";
import TableColumnResolutions from "./components/table-column-resolutions/table-column-resolutions";
import TableColumnRmaDetails from "./components/table-column-rma-details/table-column-rma-details";
import TableColumnStatus from "./components/table-column-status/table-column-status";
import bulkRmaActionStore from "../../../../stores/bulk-rma-action-store";
import rmaPagedListStore from "../../../../stores/rma-tabs-paged-list-store";
import rmsMixpanelStore from "../../../../../../../stores/rms-mixpanel-store";

export interface IRmaListTableRowProps {
    rma: RmaListItemStore;
}

const RmaListTableRow: React.FC<IRmaListTableRowProps> = observer(({ rma }) => {
    const navigate = useNavigate();
    const bulkEnabled = bulkRmaActionStore.isFeatureEnabled;
    const bulkSelectionStore = rmaPagedListStore.bulkSelectionStore;
    const selectionBlocked = bulkSelectionStore.isBlocked;
    const selected = bulkSelectionStore.isRmaSelected(rma.rmaId);

    const onOrderNumberClick = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        trackManageReturnsListEvent("d&_order", MANAGE_RETURNS_DESIGN_VERSION.V2);
    };
    const onSelect = () => {
        trackManageReturnsListEvent("d&_table_row", MANAGE_RETURNS_DESIGN_VERSION.V2);
        navigate({ pathname: `${APP_PATHS.RETURNS_NEW}/${rma.rmaId}` });
    };

    const cells = [
        ...(bulkEnabled
            ? [
                <div key="sel" className="rma-list-table-row__checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        label=""
                        size={CHECKBOX_SIZES.SMALL}
                        checked={selected}
                        disabled={selectionBlocked}
                        onChange={() => {
                            bulkSelectionStore.toggleRma(rma.rmaId);
                            rmsMixpanelStore.trackEvent("d&_bulk_toggle_row", { rmaId: rma.rmaId });
                        }}
                    />
                </div>,
              ]
            : []),
        <TableColumnRmaDetails key="rma" rma={rma} onOrderNumberClick={onOrderNumberClick} />,
        <TableColumnResolutions key="res" rma={rma} />,
        <TableColumnItems key="items" rma={rma} />,
        <TableColumnCustomer key="cust" rma={rma} />,
        <TableColumnStatus key="st" rma={rma} />,
    ];

    return (
        <TableRow
            skipWrapingCell
            cells={cells}
            onSelect={onSelect}
        />
    );
});

export default RmaListTableRow;
